import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import React from "react";

interface INodeListRowProps {
    children: React.ReactNode;
    /** Compact density used for nested rows (e.g. service-order line items inside a group). */
    density?: "default" | "compact";
    isExpanded?: boolean;
    nodeColor: string;
    onClick: (e: React.MouseEvent) => void;
}

/**
 * Shared list-button scaffold used by every expandable list-row inside a list-node.
 * Encapsulates the identical sx (radius, padding, expanded-highlight, hover) and the
 * stop-propagation onClick wrapper that was duplicated across 5+ node components.
 */
export default function NodeListRow({
    children,
    density = "default",
    isExpanded,
    nodeColor,
    onClick,
}: INodeListRowProps) {
    const isCompact = density === "compact";
    const expandedBg = isCompact ? `${nodeColor}1F` : `${nodeColor}14`;
    const hoverBg = isCompact ? `${nodeColor}1A` : `${nodeColor}14`;

    return (
        <ListItemButton
            dense
            sx={{
                borderRadius: 1,
                py: isCompact ? 0.15 : 0.25,
                px: 0.5,
                mb: isCompact ? 0.15 : 0.25,
                transition: "all 0.15s ease",
                backgroundColor: isExpanded ? expandedBg : undefined,
                borderLeft: isExpanded ? `2px solid ${nodeColor}` : "2px solid transparent",
                "&:hover": { backgroundColor: hoverBg },
            }}
            onClick={(e) => {
                e.stopPropagation();
                onClick(e);
            }}
        >
            <ListItemText disableTypography primary={children} />
        </ListItemButton>
    );
}

interface INodeRowChevronProps {
    isExpanded?: boolean;
    nodeColor: string;
    /** Optional smaller size for nested/compact rows. Defaults to 16. */
    size?: number;
}

/**
 * Shared expand/collapse chevron rendered on the right edge of a `NodeListRow`.
 * Rotates 90° when expanded; takes node colour when expanded, theme-disabled otherwise.
 */
export function NodeRowChevron({ isExpanded, nodeColor, size = 16 }: INodeRowChevronProps) {
    return (
        <ChevronRightIcon
            sx={{
                fontSize: size,
                color: isExpanded ? nodeColor : "text.disabled",
                transition: "transform 0.2s ease",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
        />
    );
}
